/**
 * orgApi.js
 * Data source for the Org Chart — backed by the real HRMS hierarchy endpoint.
 *
 *   GET /saas/hrms/current-employment/hierarchy/{empId}/{reportingType}
 *   reportingType:
 *     "MeReportingTo" — the chain from empId up to the top-level manager.
 *     "ReportingToMe" — empId's direct reports, one level down.
 *   Service code: HR02H.
 *
 * Ported into workplane-ui: this now goes through the app's shared HRMS
 * client (src/api/api.js -> hrmsGet), so it automatically carries the
 * signed-in user's Bearer token, X-Tenant-Code and X-Service-Code from
 * sessionDB — the same auth every other HRMS/ESS module uses. (The
 * standalone version hit `fetch()` directly with a hardcoded dev token.)
 *
 * `data` is an object (employee + reportingChain), not a flat array — and
 * there's no email/phone/photoUrl field at all. normalizeEmployee() below
 * maps exactly those confirmed fields; it does not guess at alternate
 * names. email/phone/photoUrl are left empty/null since the backend
 * doesn't return them yet (ContactRow in PersonPopover.jsx already hides a
 * row with no value, and Avatar already falls back to initials).
 *
 * There's no endpoint yet for "everyone with no manager" (company-wide) or
 * for name/designation search across the whole company — those stay scoped
 * to whatever's already been fetched into the client-side cache built up
 * in orgTree.js. Swap those in once their endpoints exist.
 */

import { hrmsGet } from "../../../api/api";

const SERVICE_CODE = "HR02H";
const EMP_DETAILS_SERVICE_CODE = "HR01S";

function normalizeEmployee(e) {
  if (!e) return null;
  return {
    ...e,
    name: e.empName,
    email: e.email ?? "",
    phone: e.phone ?? "",
    photoUrl: e.photoUrl ?? null,
  };
}

// GET /emp-info/search/{empId} returns a differently-shaped record than the
// hierarchy endpoint: no empName/designation/department, but firstName /
// middleName / lastName / fullName, plus the email/phone the hierarchy
// endpoint never has. hasEmpPic flags whether a photo exists, but no URL is
// returned yet, so photoUrl stays null (Avatar falls back to initials).
function normalizeEmployeeDetails(e) {
  if (!e) return null;
  const name = e.fullName || [e.firstName, e.middleName, e.lastName].filter(Boolean).join(" ");
  return {
    ...e,
    name,
    email: e.email ?? "",
    phone: e.phone ?? "",
    photoUrl: null,
  };
}

async function getHierarchy(empId, reportingType) {
  // hrmsGet already prefixes HRMS_BASE_URL (`${API_BASE_URL}/saas/hrms`),
  // injects the auth headers, parses JSON and throws on a non-2xx status.
  const body = await hrmsGet(
    `/current-employment/hierarchy/${empId}/${reportingType}`,
    SERVICE_CODE
  );
  const data = body?.data ?? {};
  return {
    self: normalizeEmployee(data.employee),
    chain: (data.reportingChain ?? []).map(normalizeEmployee).filter(Boolean),
  };
}

/**
 * The chain from empId up to the top-level manager: [self, manager,
 * manager's manager, ..., top]. Fetched in full on every re-centre —
 * OrgChartPage.jsx's expand-up chevron just reveals more of this locally
 * (see getVisibleChain in orgTree.js), no extra round-trip needed.
 */
export async function fetchManagerChain(empId) {
  const { self, chain } = await getHierarchy(empId, "MeReportingTo");
  return [self, ...chain].filter(Boolean);
}

/** empId's entire downward subtree (every level below them, flattened —
 * see mergeReports in orgTree.js for how it's re-nested by managerId).
 * Re-fetched each time a card not already in the cache is expanded (see
 * toggleDown in OrgChartPage.jsx). */
export async function fetchDirectReports(empId) {
  const { chain } = await getHierarchy(empId, "ReportingToMe");
  return chain;
}

/**
 * GET /saas/hrms/emp-info/search/{empId} — full employee record, fetched
 * when a card is opened in the Person Detail Popover so it can show
 * phone/email that the hierarchy endpoint above doesn't return.
 * Service code: HR01S.
 */
export async function fetchEmployeeDetails(empId) {
  const body = await hrmsGet(`/emp-info/search/${empId}`, EMP_DETAILS_SERVICE_CODE);
  return normalizeEmployeeDetails(body?.data ?? body);
}
